/**
 * Order cancel card — shows cancellation prompt + grace-period countdown,
 * and a confirmation step with refund messaging.
 */

import { useTranslation } from 'react-i18next'
import { Button, Card, CardContent } from '@/components/ui'

interface OrderCancelCardProps {
  showCancelConfirm: boolean
  isCancelling: boolean
  cancelError: string | null
  timeLeftMs: number
  onShowConfirm: () => void
  onCancel: () => void
  onKeep: () => void
}

/** Format countdown as m:ss */
function formatCountdown(ms: number) {
  const totalSec = Math.ceil(ms / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${min}:${sec.toString().padStart(2, '0')}`
}

export function OrderCancelCard({
  showCancelConfirm,
  isCancelling,
  cancelError,
  timeLeftMs,
  onShowConfirm,
  onCancel,
  onKeep,
}: OrderCancelCardProps) {
  const { t } = useTranslation()
  const formatted = formatCountdown(timeLeftMs)

  return (
    <Card className="mb-6 border-orange-200 bg-orange-50/50 dark:border-orange-900 dark:bg-orange-950/20">
      <CardContent className="py-4">
        {!showCancelConfirm ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{t('common:changedYourMind')}</p>
              <p className="text-xs text-muted-foreground">
                {t('common:cancelWithinTime', { time: formatted })}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-orange-600 border-orange-300 hover:bg-orange-100"
              onClick={onShowConfirm}
            >
              {t('common:cancelOrder')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium">{t('common:areYouSureCancelOrder')}</p>
            <p className="text-xs text-muted-foreground">
              {t('common:paymentWillBeRefunded', { time: formatted })}
            </p>
            {cancelError && (
              <p className="text-xs text-destructive">{cancelError}</p>
            )}
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={onCancel}
                disabled={isCancelling}
              >
                {isCancelling ? t('common:cancelling') : t('common:yesCancelOrder')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onKeep}
                disabled={isCancelling}
              >
                {t('common:keepOrder')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
